<template>
    <a-card>
        <div id="whatsapp-channel-wizard">
            <a-steps v-model="current" type="navigation" :style="stepStyle">
                <a-step :status="getStatus(0)" :title="$t('wp.agreement')" :disabled="current !== 0" />
                <a-step :status="getStatus(1)" :title="$t('wp.activating')" :disabled="current !== 1" />
                <a-step :status="getStatus(2)" :title="$t('wp.authenticating')" :disabled="current !== 2" />
                <a-step :status="getStatus(3)" :title="$t('wp.ready')" :disabled="current !== 3" />
            </a-steps>

            <div class="d-flex justify-content-center">
                <div v-if="current === 0" class="step step0">
                    <Agreement @agree="agree" />
                </div>
                <div v-if="current === 1" class="step step2">
                    <Activation :activated="activationCompleted" />
                </div>
                <div v-if="current === 2" class="step step3">
                    <QRCodeAuthentication :channel-id="channel._id" :channel="channel" />
                </div>
                <div v-if="current === 3" class="step step4">
                    <WPStatusViewer :channel-id="channel._id" :channel="channel" />
                </div>
            </div>
        </div>
    </a-card>
</template>

<script>
import "./style.scss";

import { mapState } from "vuex";

import Agreement from "../../../../components/whatsapp/Agreement.vue";
import Purchase from "../../../../components/whatsapp/Purchase.vue";
import Activation from "../../../../components/whatsapp/Activation.vue";
import QRCodeAuthentication from "../../../../components/whatsapp/QRCodeAuthentication.vue";
import WPStatusViewer from "../../../../components/whatsapp/WPStatusViewer.vue";
import { sendSocketMessage } from "../../../../utils/socketStream";
import log2 from "../../../../utils/log2";
import CONFIG from "../../../../config/config";

const log = log2("WhatsappWizard");
const LSTATES = CONFIG.WHATSAPP.LAUNCH_STATES;
export default {
    name: "WhatsappWizard",
    components: {
        Agreement,
        Purchase,
        Activation,
        QRCodeAuthentication,
        WPStatusViewer,
    },
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            current: 0,
            stepStyle: {
                marginBottom: "60px",
                boxShadow: "0px -1px 0 0 #e8e8e8 inset",
            },
            waiting: false,
            agreementAccepted: false,
            isV2Channel: this.channel.type === "whatsapp-v2",
        };
    },
    computed: {
        ...mapState({
            settings: (state) => state.setting.settings,
            site: (state) => state.site.site,
            initialized: (state) => state.whatsapp.info.initialized,
            channelInfo: (state) => state.whatsapp.channelInfo,
        }),
    },
    methods: {
        getChannelInfo() {
            const fallbackStatus = this.isV2Channel
                ? {
                      initialized: false,
                      status: {
                          created: false,
                          ready: false,
                          dead: false,
                          status: {
                              STATE: "UNKNOWN",
                              LAUNCH_STATE: "NOT_CREATED",
                          },
                          error: undefined,
                          purchased: true,
                      },
                  }
                : {
                      initialized: false,
                      status: {
                          created: false,
                          ready: false,
                          dead: false,
                          status: {
                              STATE: "UNKNOWN",
                              LAUNCH_STATE: "NOT_CREATED",
                          },
                          error: undefined,
                          phoneConnected: false,
                          batteryLevel: 100,
                          purchased: true,
                      },
                  };
            return this.channelInfo[this.channel._id] || fallbackStatus;
        },
        getWhatsappStatus() {
            const info = this.getChannelInfo();
            return info.status;
        },
        getStatus(order) {
            if (order < this.current) return "finish";
            if (order === this.current) return "process";
            return "wait";
        },
        fetchWhatsappStatus() {
            sendSocketMessage("user get-whatsapp-status", {
                siteId: this.site._id,
                channelId: this.channel._id,
            });
        },
        agree() {
            this.agreementAccepted = true;
            this.current = 1;
            localStorage.setItem("wp-agreement-accepted", "true");
        },
        activationCompleted() {
            this.current = 2;
        },
        purchaseCompleted() {
            this.current = 1;
        },
        reCalculateStep(newStatus) {
            newStatus = newStatus || this.getWhatsappStatus();
            const launchState = this.isV2Channel ? undefined : newStatus?.status?.LAUNCH_STATE;
            const botState = this.isV2Channel ? newStatus.state : newStatus.status?.STATE;
            log("reCalculateStep", { newStatus, launchState, botState });
            const isCompleted = launchState === "READY" || botState === "CONNECTED" || botState === "READY";
            const isQRStep = this.isV2Channel
                ? ["QR_CODE_GENERATED", "QR_CODE_GENERATING"].includes(botState)
                : botState !== "CONNECTED" &&
                  [LSTATES.QR_CODE_GENERATING, LSTATES.QR_CODE_GENERATED, LSTATES.AUTHENTICATING].includes(launchState);
            log("reCalculateStep", {
                newStatus,
                isCompleted,
                isQRStep,
            });

            if (!newStatus.purchased) {
                if (localStorage.getItem("wp-agreement-accepted") === "true") {
                    this.current = 1;
                } else {
                    this.current = 0;
                }
            } else {
                this.agreementAccepted = true;

                if (isCompleted) {
                    this.current = 3;
                    this.$emit("complated");
                } else if (isQRStep) {
                    this.current = 2;
                } else {
                    this.current = 1;
                }
            }
        },
    },
    mounted() {
        log("mounted");
        this.fetchWhatsappStatus();
        const status = this.getWhatsappStatus();
        if (!status.purchased) {
            if (localStorage.getItem("wp-agreement-accepted") === "true") {
                this.current = 1;
                this.agreementAccepted = true;
            } else {
                this.current = 0;
            }
        }
        this.reCalculateStep();
        this.timerId = setInterval(() => {
            this.fetchWhatsappStatus();
        }, 3000);
    },
    beforeDestroy() {
        clearInterval(this.timerId);
    },
    watch: {
        channelInfo(newChannelInfo) {
            const newStatus = this.getWhatsappStatus();
            log("newStatus changed", newStatus);
            this.reCalculateStep(newStatus);
        },
    },
};
</script>

<style scoped></style>
